import { Play } from "lucide-react";
import { toast } from "sonner";

import { SourceOperationsSheet } from "@/components/sources/source-operations-sheet";
import { SourcePauseMenu } from "@/components/sources/source-pause-menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useOperatorStatus } from "@/hooks/useOperatorStatus";
import { useRunSource, useSources } from "@/hooks/useSources";
import type { OperatorSourceStatus } from "@/lib/api";

export default function OperatorSchedulesPage() {
  const sourcesQ = useSources();
  const statusQ = useOperatorStatus();
  const runSource = useRunSource();
  const sourcesById = new Map((sourcesQ.data ?? []).map((source) => [source.id, source]));
  const sourceStates = statusQ.data?.source_states ?? [];

  const runNow = (sourceId: string) => {
    runSource.mutate(sourceId, {
      onSuccess: (result) => {
        const itemLabel = `captured ${result.inbox_items_created} item${result.inbox_items_created === 1 ? "" : "s"}`;
        const processingLabel =
          result.processing_status === "skipped"
            ? "processing skipped"
            : `processing ${result.processing_status}: ${result.processing_converted_count} converted`;
        toast.success(`${itemLabel}; ${processingLabel}`);
      },
      onError: (err) => toast.error(`run failed: ${err.message}`),
    });
  };

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border/70 px-6 py-4">
        <h1 className="text-xl font-semibold">Scrape Runtime</h1>
        <p className="text-xs text-muted-fg">
          Content scraper worker state, source cadence, and manual runtime controls.
        </p>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-3">
          {sourceStates.map((state: OperatorSourceStatus) => (
            <section
              key={state.source.id}
              className="rounded-md border border-border/60 bg-bg/30 p-3"
            >
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="text-sm font-medium">
                    {state.source.display_name}
                  </h2>
                  <div className="font-mono text-[11px] text-muted-fg">
                    {state.source.id}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">
                    {state.runtime.mode}
                  </Badge>
                  <Badge variant={state.worker.scheduled ? "success" : "outline"}>
                    {state.worker.scheduled ? "scheduled" : "not scheduled"}
                  </Badge>
                  <Badge variant="outline">{state.worker.cadence ?? state.source.cadence}</Badge>
                  {state.source.is_schedule_paused ? (
                    <Badge variant="warn">paused</Badge>
                  ) : null}
                </div>
              </div>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => runNow(state.source.id)}
                  disabled={runSource.isPending}
                  title="Run this source now"
                >
                  <Play />
                  Run now
                </Button>
                {sourcesById.get(state.source.id) ? (
                  <>
                    <SourceOperationsSheet source={sourcesById.get(state.source.id)!} />
                    <SourcePauseMenu source={sourcesById.get(state.source.id)!} />
                  </>
                ) : null}
              </div>
              <div className="rounded border border-border/40 bg-muted/20 px-3 py-2 text-xs text-muted-fg">
                Worker {state.worker.configured ? "synced" : "not synced"}
                {state.worker.service_url ? ` at ${state.worker.service_url}` : ""}
                {state.worker.error ? ` · ${state.worker.error}` : ""}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
